import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogSurface,
    Spinner,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { customColors, customFonts } from '../../styles';

const useStyles = makeStyles({
    surface: {
        maxWidth: '480px',
        ...shorthands.borderRadius('12px'),
        ...shorthands.padding('32px', '36px'),
    },
    body: {
        fontFamily: customFonts.TitleFont,
        fontWeight: '500',
        fontSize: '24px',
        lineHeight: '1.4',
        color: tokens.colorNeutralForeground1,
        display: 'flex',
    },
    bodyMessage: {
        fontWeight: '500',
        fontSize: '24px',
        lineHeight: '1.4',
        color: tokens.colorNeutralForeground1,
    },
    actions: {
        paddingTop: '24px',
        display: 'flex',
        justifyContent: 'flex-end',
        ...shorthands.gap('12px'),
    },
    button: {
        fontFamily: customFonts.Lato,
        fontWeight: 600,
        fontSize: '14px',
        minWidth: '100px',
        ...shorthands.padding('5px', '24px'),
        ...shorthands.borderRadius('6px'),
        transition: 'all 0.3s ease',
    },
    confirmButton: {
        backgroundColor: customColors.white,
        color: customColors.warningRed,
        ...shorthands.border('1.5px', 'solid', customColors.warningRed),
        ':hover': {
            backgroundColor: customColors.warningRed,
            color: customColors.white,
            ...shorthands.borderColor(customColors.warningRed),
        },
    },
    cancelButton: {
        backgroundColor: customColors.white,
        color: tokens.colorNeutralForeground1,
        ...shorthands.border('1.5px', 'solid', tokens.colorNeutralStroke1),
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground3,
        },
    },
});

interface ConfirmDialogProps {
    open: boolean;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmButtonText?: string;
    isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    message,
    onConfirm,
    onCancel,
    confirmButtonText = 'Delete',
    isLoading = false,
}) => {
    const styles = useStyles();

    return (
        <Dialog
            open={open}
            onOpenChange={(_e, data) => {
                if (!data.open) onCancel();
            }}
        >
            <DialogSurface className={styles.surface}>
                <DialogBody className={styles.body}>
                    <div className={styles.bodyMessage}>{message}</div>
                </DialogBody>
                <DialogActions className={styles.actions}>
                    <Button
                        className={mergeClasses(styles.button, styles.cancelButton)}
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        className={mergeClasses(styles.button, styles.confirmButton)}
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner size="tiny" /> : confirmButtonText}
                    </Button>
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};

export default ConfirmDialog;
